import { groupArrayByKeys } from '@campus/utils';
import { createEntityAdapter, Dictionary, EntityAdapter, EntityState, Update } from '@ngrx/entity';
import { createActionGroup, createFeature, createReducer, createSelector, on, props } from '@ngrx/store';
import { EduContentTOCNavigationInterface } from '../../+models';

// Must match the `eduContentTOCNavigations` field in BookDataResponseInterface:
// the book-data effect derives the target state slice name from the field name.
export const NAME = 'eduContentTOCNavigations';

export interface State extends EntityState<EduContentTOCNavigationInterface> {
  loadedForBook: Dictionary<boolean>;
}

export const adapter: EntityAdapter<EduContentTOCNavigationInterface> =
  createEntityAdapter<EduContentTOCNavigationInterface>();

export const initialState: State = adapter.getInitialState({
  loadedForBook: {},
});

export const actions = createActionGroup({
  source: 'EduContentTocNavigations',
  events: {
    // Event names map to the same action `type` strings the class-based actions
    // used (e.g. `[EduContentTocNavigations] Add EduContentTocNavigations for Book`),
    // so devtools history and the undo meta-reducer keep working unchanged.
    'Add EduContentTocNavigations for Book': props<{
      bookId: number;
      eduContentTOCNavigations: EduContentTOCNavigationInterface[];
    }>(),
    'Upsert EduContentTocNavigations': props<{ eduContentTOCNavigations: EduContentTOCNavigationInterface[] }>(),
    'Update EduContentTocNavigation': props<{ eduContentTOCNavigation: Update<EduContentTOCNavigationInterface> }>(),
    'Clear EduContentTocNavigations for Book': props<{ bookId: number }>(),
  },
});

export const feature = createFeature({
  name: NAME,
  reducer: createReducer(
    initialState,
    on(actions.addEduContentTocNavigationsForBook, (state, { bookId, eduContentTOCNavigations }): State => {
      const newState = adapter.upsertMany(eduContentTOCNavigations, state);
      return {
        ...newState,
        loadedForBook: {
          ...newState.loadedForBook,
          [bookId]: true,
        },
      };
    }),
    on(
      actions.upsertEduContentTocNavigations,
      (state, { eduContentTOCNavigations }): State => adapter.upsertMany(eduContentTOCNavigations, state)
    ),
    on(
      actions.updateEduContentTocNavigation,
      (state, { eduContentTOCNavigation }): State => adapter.updateOne(eduContentTOCNavigation, state)
    ),
    on(actions.clearEduContentTocNavigationsForBook, (state, { bookId }): State => {
      const loadedForBook = { ...state.loadedForBook };
      delete loadedForBook[bookId];
      return { ...state, loadedForBook };
    })
  ),
  extraSelectors: ({ selectEduContentTOCNavigationsState, selectEntities, selectLoadedForBook }) => {
    const { selectAll, selectTotal, selectIds } = adapter.getSelectors(selectEduContentTOCNavigationsState);

    return {
      selectAll,
      selectTotal,
      selectIds,

      selectById: (id: number) => createSelector(selectEntities, (entities) => entities[id]),

      /**
       * returns a dictionary of navigation entries grouped by eduContentTocId
       * @example
       * navigations$: Dictionary<EduContentTOCNavigationInterface[]> = this.store.pipe(
       *  select(EduContentTocNavigationFeature.feature.selectByEduContentTOCId)
       * );
       **/
      selectByEduContentTOCId: createSelector(
        selectAll,
        (navigations): Dictionary<EduContentTOCNavigationInterface[]> =>
          groupArrayByKeys(navigations, ['eduContentTOCId'])
      ),

      selectForTOC: (tocId: number) =>
        createSelector(selectAll, (navigations) =>
          navigations
            .filter((navigation) => navigation.eduContentTOCId === tocId)
            .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
        ),

      selectIsBookLoaded: (bookId: number) =>
        createSelector(selectLoadedForBook, (loadedForBook) => !!loadedForBook[bookId]),
    };
  },
});
